import { api } from "./api";
import { useAuthStore } from "./auth";
import type { Paginated, PageSize } from "./pagination";

export interface AuditEntry {
  audit_id: number;
  event_type: string;
  actor_type: string;
  actor_id: string | null;
  actor_ip: string | null;
  target_type: string | null;
  target_id: string | null;
  action: string;
  outcome: "success" | "failure" | "denied";
  details: Record<string, unknown> | null;
  correlation_id: string | null;
  occurred_at: string;
}

export interface AuditFilters {
  event_type?: string;
  actor_id?: string;
  target_type?: string;
  target_id?: string;
  outcome?: "success" | "failure" | "denied";
  from?: string;
  to?: string;
  page?: number;
  page_size?: PageSize;
}

function buildQuery(filters: AuditFilters): Record<string, string | number | boolean | undefined> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== undefined && v !== ""),
  );
}

export interface AuditFacets {
  event_types: string[];
  target_types: string[];
  actor_ids: string[];
}

export const auditApi = {
  list: (filters: AuditFilters = {}) =>
    api.get<Paginated<AuditEntry>>("/audit", { query: buildQuery(filters) }),

  facets: () => api.get<AuditFacets>("/audit/facets"),

  /** Returns a CSV download URL that includes the bearer token in the query.
   *  Browser then triggers the download directly. */
  buildExportUrl(filters: AuditFilters = {}): string {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(buildQuery(filters))) {
      params.set(k, String(v));
    }
    return `/api/admin/audit/export?${params.toString()}`;
  },

  async downloadExport(filters: AuditFilters = {}): Promise<void> {
    // Fetch with auth header, then trigger browser download from blob URL.
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(buildQuery(filters))) {
      params.set(k, String(v));
    }
    const token = useAuthStore.getState().token;
    const response = await fetch(`/api/admin/audit/export?${params.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download =
      response.headers
        .get("content-disposition")
        ?.match(/filename="([^"]+)"/)?.[1] ?? "tray-audit.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};
